import React, { useState, useEffect } from 'react';
import { Download, Zap, Shield, Package, Terminal, TrendingUp, Clock, Database, Book, Code, Users, Menu, X, ChevronRight, Copy, Check, ExternalLink, FileText, Boxes, GitBranch } from 'lucide-react';
import { Github } from "lucide-react";
import { Helmet } from 'react-helmet';

export default function RSnappWebsite() {
  const [scrollY, setScrollY] = useState(0);
  const [activeFeature, setActiveFeature] = useState(0);
  const [currentPage, setCurrentPage] = useState('home');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [copiedCommand, setCopiedCommand] = useState('');
  const [activeDocSection, setActiveDocSection] = useState('getting-started');

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveFeature((prev) => (prev + 1) % 4);
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  const copyToClipboard = (text, id) => {
    navigator.clipboard.writeText(text);
    setCopiedCommand(id);
    setTimeout(() => setCopiedCommand(''), 2000);
  };

  const styles = {
    container: {
      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
      color: '#1a1a1a',
      overflow: 'hidden',
      background: '#ffffff',
    },
    header: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      zIndex: 1000,
      background: scrollY > 50 ? 'rgba(255, 255, 255, 0.98)' : 'rgba(255, 255, 255, 0.95)',
      backdropFilter: 'blur(20px)',
      borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
      transition: 'all 0.3s ease',
      padding: '16px 0',
      boxShadow: scrollY > 50 ? '0 4px 20px rgba(0, 0, 0, 0.08)' : 'none',
    },
    nav: {
      maxWidth: '1400px',
      margin: '0 auto',
      padding: '0 40px',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
    },
    logo: {
      fontSize: '32px',
      fontWeight: '900',
      background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 50%, #d946ef 100%)',
      WebkitBackgroundClip: 'text',
      WebkitTextFillColor: 'transparent',
      letterSpacing: '-1.5px',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
    },
    navLinks: {
      display: 'flex',
      gap: '48px',
      alignItems: 'center',
    },
    navLink: (active) => ({
      color: active ? '#6366f1' : '#64748b',
      textDecoration: 'none',
      fontSize: '15px',
      fontWeight: '600',
      transition: 'all 0.2s ease',
      cursor: 'pointer',
      padding: '8px 16px',
      borderRadius: '8px',
      background: active ? 'rgba(99, 102, 241, 0.1)' : 'transparent',
    }),
    hero: {
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 50%, #d946ef 100%)',
      position: 'relative',
      overflow: 'hidden',
      padding: '120px 40px 80px',
    },
    heroPattern: {
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      opacity: 0.15,
      backgroundImage: `radial-gradient(circle at 25% 25%, rgba(255,255,255,0.3) 2%, transparent 0%), 
                        radial-gradient(circle at 75% 75%, rgba(255,255,255,0.3) 2%, transparent 0%)`,
      backgroundSize: '60px 60px',
    },
    heroContent: {
      maxWidth: '1200px',
      width: '100%',
      textAlign: 'center',
      position: 'relative',
      zIndex: 1,
    },
    badge: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '8px',
      padding: '8px 20px',
      background: 'rgba(255, 255, 255, 0.15)',
      backdropFilter: 'blur(10px)',
      borderRadius: '50px',
      border: '1px solid rgba(255, 255, 255, 0.2)',
      color: '#ffffff',
      fontSize: '14px',
      fontWeight: '600',
      marginBottom: '32px',
    },
    heroTitle: {
      fontSize: '80px',
      fontWeight: '900',
      color: '#ffffff',
      marginBottom: '28px',
      lineHeight: '1.1',
      letterSpacing: '-3px',
    },
    heroSubtitle: {
      fontSize: '26px',
      color: 'rgba(255, 255, 255, 0.95)',
      marginBottom: '48px',
      fontWeight: '400',
      lineHeight: '1.6',
      maxWidth: '800px',
      margin: '0 auto 48px',
    },
    heroButtons: {
      display: 'flex',
      gap: '20px',
      justifyContent: 'center',
      marginBottom: '64px',
      flexWrap: 'wrap',
    },
    primaryButton: {
      padding: '20px 52px',
      fontSize: '18px',
      fontWeight: '700',
      background: '#ffffff',
      color: '#6366f1',
      border: 'none',
      borderRadius: '14px',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      boxShadow: '0 12px 40px rgba(0, 0, 0, 0.25)',
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
    },
    secondaryButton: {
      padding: '20px 52px',
      fontSize: '18px',
      fontWeight: '700',
      background: 'rgba(255, 255, 255, 0.1)',
      color: '#ffffff',
      border: '2px solid rgba(255, 255, 255, 0.4)',
      borderRadius: '14px',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      backdropFilter: 'blur(10px)',
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
    },
    codeBlock: {
      background: 'rgba(0, 0, 0, 0.4)',
      backdropFilter: 'blur(20px)',
      padding: '32px 40px',
      borderRadius: '16px',
      display: 'inline-block',
      fontFamily: "'Fira Code', 'Courier New', monospace",
      fontSize: '19px',
      color: '#ffffff',
      textAlign: 'left',
      border: '1px solid rgba(255, 255, 255, 0.2)',
      boxShadow: '0 24px 80px rgba(0, 0, 0, 0.4)',
      position: 'relative',
      minWidth: '500px',
    },
    statsSection: {
      background: 'linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)',
      padding: '100px 40px',
    },
    statsContainer: {
      maxWidth: '1400px',
      margin: '0 auto',
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
      gap: '32px',
    },
    statCard: {
      textAlign: 'center',
      padding: '48px 32px',
      background: '#ffffff',
      borderRadius: '20px',
      border: '1px solid rgba(0, 0, 0, 0.06)',
      transition: 'all 0.3s ease',
      boxShadow: '0 4px 16px rgba(0, 0, 0, 0.04)',
    },
    statNumber: {
      fontSize: '64px',
      fontWeight: '900',
      background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 50%, #d946ef 100%)',
      WebkitBackgroundClip: 'text',
      WebkitTextFillColor: 'transparent',
      marginBottom: '16px',
      letterSpacing: '-2px',
    },
    statLabel: {
      fontSize: '18px',
      color: '#64748b',
      fontWeight: '600',
    },
    section: {
      padding: '140px 40px',
      background: '#ffffff',
    },
    sectionAlt: {
      padding: '140px 40px',
      background: 'linear-gradient(180deg, #f8fafc 0%, #ffffff 100%)',
    },
    container: {
      maxWidth: '1400px',
      margin: '0 auto',
    },
    sectionTitle: {
      fontSize: '56px',
      fontWeight: '900',
      textAlign: 'center',
      marginBottom: '24px',
      background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 50%, #d946ef 100%)',
      WebkitBackgroundClip: 'text',
      WebkitTextFillColor: 'transparent',
      letterSpacing: '-2px',
    },
    sectionSubtitle: {
      fontSize: '22px',
      color: '#64748b',
      textAlign: 'center',
      marginBottom: '80px',
      maxWidth: '800px',
      margin: '0 auto 80px',
      lineHeight: '1.7',
    },
    featuresGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(450px, 1fr))',
      gap: '32px',
    },
    featureCard: (isActive) => ({
      padding: '48px',
      background: isActive 
        ? 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 50%, #d946ef 100%)' 
        : '#ffffff',
      borderRadius: '24px',
      border: `2px solid ${isActive ? 'transparent' : 'rgba(0, 0, 0, 0.08)'}`,
      transition: 'all 0.4s ease',
      boxShadow: isActive 
        ? '0 24px 80px rgba(99, 102, 241, 0.5)' 
        : '0 4px 20px rgba(0, 0, 0, 0.06)',
      transform: isActive ? 'translateY(-12px)' : 'translateY(0)',
      cursor: 'pointer',
    }),
    featureIcon: (isActive) => ({
      width: '72px',
      height: '72px',
      borderRadius: '18px',
      background: isActive 
        ? 'rgba(255, 255, 255, 0.2)' 
        : 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      marginBottom: '28px',
      color: '#ffffff',
      transition: 'all 0.4s ease',
    }),
    featureTitle: (isActive) => ({
      fontSize: '28px',
      fontWeight: '800',
      marginBottom: '16px',
      color: isActive ? '#ffffff' : '#1e293b',
      letterSpacing: '-0.5px',
    }),
    featureDescription: (isActive) => ({
      fontSize: '17px',
      color: isActive ? 'rgba(255, 255, 255, 0.95)' : '#64748b',
      lineHeight: '1.8',
    }),
    comparisonTable: {
      width: '100%',
      borderCollapse: 'separate',
      borderSpacing: '0',
      background: '#ffffff',
      borderRadius: '20px',
      overflow: 'hidden',
      boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08)',
    },
    tableHeader: {
      background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
      color: '#ffffff',
      padding: '28px 32px',
      textAlign: 'left',
      fontSize: '17px',
      fontWeight: '700',
    },
    tableRow: {
      background: '#ffffff',
      transition: 'all 0.2s ease',
      borderBottom: '1px solid rgba(0, 0, 0, 0.05)',
    },
    tableCell: {
      padding: '28px 32px',
      fontSize: '16px',
    },
    footer: {
      background: '#0f172a',
      color: '#ffffff',
      padding: '80px 40px 40px',
    },
    footerContainer: {
      maxWidth: '1400px',
      margin: '0 auto',
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
      gap: '48px',
      marginBottom: '48px',
    },
    footerTitle: {
      fontSize: '19px',
      fontWeight: '800',
      marginBottom: '24px',
      color: '#ffffff',
    },
    footerLink: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      color: '#94a3b8',
      textDecoration: 'none',
      marginBottom: '16px',
      fontSize: '15px',
      transition: 'color 0.2s ease',
      cursor: 'pointer',
    },
    copyright: {
      textAlign: 'center',
      paddingTop: '48px',
      borderTop: '1px solid rgba(255, 255, 255, 0.1)',
      color: '#64748b',
      fontSize: '15px',
    },
    docsContainer: {
      maxWidth: '1400px',
      margin: '0 auto',
      padding: '120px 40px 80px',
      display: 'grid',
      gridTemplateColumns: '280px 1fr',
      gap: '64px',
    },
    docsSidebar: {
      position: 'sticky',
      top: '100px',
      height: 'fit-content',
    },
    docsContent: {
      maxWidth: '900px',
    },
    docSection: {
      marginBottom: '64px',
    },
    docTitle: {
      fontSize: '40px',
      fontWeight: '900',
      marginBottom: '16px',
      color: '#1e293b',
      letterSpacing: '-1px',
    },
    docSubtitle: {
      fontSize: '24px',
      fontWeight: '800',
      marginTop: '48px',
      marginBottom: '16px',
      color: '#334155',
    },
    docText: {
      fontSize: '17px',
      lineHeight: '1.8',
      color: '#475569',
      marginBottom: '20px',
    },
    codeSnippet: {
      background: '#1e293b',
      color: '#e2e8f0',
      padding: '24px',
      borderRadius: '12px',
      fontFamily: "'Fira Code', monospace",
      fontSize: '15px',
      overflow: 'auto',
      marginBottom: '24px',
      position: 'relative',
      border: '1px solid rgba(255, 255, 255, 0.1)',
    },
    sidebarLink: (active) => ({
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      padding: '12px 16px',
      marginBottom: '4px',
      borderRadius: '10px',
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      background: active ? 'rgba(99, 102, 241, 0.1)' : 'transparent',
      color: active ? '#6366f1' : '#64748b',
      fontWeight: active ? '700' : '500',
      fontSize: '15px',
    }),
    commandBox: {
      background: '#f1f5f9',
      padding: '20px 24px',
      borderRadius: '12px',
      fontFamily: "'Fira Code', monospace",
      fontSize: '15px',
      marginBottom: '24px',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      border: '2px solid #e2e8f0',
    },
    copyButton: {
      background: '#6366f1',
      color: '#ffffff',
      border: 'none',
      padding: '8px 16px',
      borderRadius: '8px',
      cursor: 'pointer',
      fontSize: '13px',
      fontWeight: '600',
      display: 'flex',
      alignItems: 'center',
      gap: '6px',
      transition: 'all 0.2s ease',
    },
  };

  const features = [
    { 
      icon: Zap, 
      title: '10 Parallel Downloads', 
      description: 'Download multiple packages simultaneously with configurable parallelism (default: 10). Significantly reduces installation time for projects with many dependencies.' 
    },
    { 
      icon: Database, 
      title: 'Smart Caching System', 
      description: 'Persistent cache in ~/.snapp/cache provides 90% faster reinstalls. Works offline for cached packages. Reduces bandwidth usage and improves reliability.' 
    },
    { 
      icon: Shield, 
      title: 'SHA-512 Integrity', 
      description: 'Every package is cryptographically verified using SHA-512 hashing. Detects corrupted downloads and prevents package tampering for maximum security.' 
    },
    { 
      icon: TrendingUp, 
      title: 'Full Semver Support', 
      description: 'Complete support for all npm semantic versioning patterns including ^, ~, >=, <=, >, <, exact versions, and ranges. Fully compatible with package.json.' 
    },
    { 
      icon: GitBranch, 
      title: 'Dependency Graph', 
      description: 'Intelligent dependency resolution with cycle detection. Analyzes the entire dependency tree to determine optimal installation order and warn about circular dependencies.' 
    },
    { 
      icon: Terminal, 
      title: 'Binary Linking', 
      description: 'Automatic setup of CLI tools with proper binary linking. Creates symlinks in node_modules/.bin for all package executables with correct permissions.' 
    },
    { 
      icon: Clock, 
      title: 'Auto-Retry Logic', 
      description: 'Automatic retry on network failures with 3 attempts and exponential backoff. Timeout protection (30s metadata, 60s downloads) for unreliable networks.' 
    },
    { 
      icon: Code, 
      title: 'Debug Mode', 
      description: 'Comprehensive debug logging with SNAPP_DEBUG=1. See HTTP requests, cache operations, version resolution steps, and detailed error stack traces.' 
    },
  ];

  const docsSections = [
    { id: 'getting-started', title: 'Getting Started', icon: Zap },
    { id: 'installation', title: 'Installation', icon: Download },
    { id: 'commands', title: 'Commands', icon: Terminal },
    { id: 'configuration', title: 'Configuration', icon: Code },
    { id: 'features', title: 'Features', icon: Package },
    { id: 'troubleshooting', title: 'Troubleshooting', icon: Book },
  ];

  const renderHome = () => (
    <>
      <section style={styles.hero}>
        <div style={styles.heroPattern}></div>
        <div style={styles.heroContent}>
          <div style={styles.badge}>
            <Zap size={16} />
            <span>Version 1.0.1 • Production Ready</span>
          </div>
          <h1 style={styles.heroTitle}>R_SNAPP</h1>
          <p style={styles.heroSubtitle}>
            The lightning-fast Node.js package manager with intelligent caching,
            parallel downloads, and cryptographic security. Install packages 90% faster.
          </p>
          <div style={styles.heroButtons}>
            <button 
              style={styles.primaryButton}
              onClick={() => setCurrentPage('docs')}
              onMouseEnter={(e) => {
                e.target.style.transform = 'translateY(-3px)';
                e.target.style.boxShadow = '0 16px 48px rgba(0, 0, 0, 0.35)';
              }}
              onMouseLeave={(e) => {
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = '0 12px 40px rgba(0, 0, 0, 0.25)';
              }}
            >
              <Download size={22} />
              Get Started
            </button>
            <button 
              style={styles.secondaryButton}
              onClick={() => window.open('https://github.com/jnt48/R-SNAPP', '_blank')}
              onMouseEnter={(e) => {
                e.target.style.background = 'rgba(255, 255, 255, 0.2)';
                e.target.style.borderColor = 'rgba(255, 255, 255, 0.6)';
              }}
              onMouseLeave={(e) => {
                e.target.style.background = 'rgba(255, 255, 255, 0.1)';
                e.target.style.borderColor = 'rgba(255, 255, 255, 0.4)';
              }}
            >
              <Github size={22} />
              View on GitHub
            </button>
          </div>
          <div style={styles.codeBlock}>
            <div style={{ color: '#94a3b8', marginBottom: '12px', fontSize: '15px' }}>
              # Install globally
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span>$ npm install -g r_snapp</span>
              <button
                onClick={() => copyToClipboard('npm install -g r_snapp', 'install')}
                style={{
                  background: 'rgba(255, 255, 255, 0.15)',
                  border: 'none',
                  color: '#ffffff',
                  padding: '8px 16px',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                }}
              >
                {copiedCommand === 'install' ? <Check size={16} /> : <Copy size={16} />}
                {copiedCommand === 'install' ? 'Copied!' : 'Copy'}
              </button>
            </div>
            <div style={{ color: '#34d399', marginTop: '12px', fontSize: '16px' }}>
              ✓ Installation complete
            </div>
          </div>
        </div>
      </section>

      <section style={styles.statsSection}>
        <div style={styles.statsContainer}>
          <div 
            style={styles.statCard}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-10px)';
              e.currentTarget.style.boxShadow = '0 20px 60px rgba(0, 0, 0, 0.15)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 4px 16px rgba(0, 0, 0, 0.04)';
            }}
          >
            <div style={styles.statNumber}>90%</div>
            <div style={styles.statLabel}>Faster Reinstalls</div>
          </div>
          <div 
            style={styles.statCard}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-10px)';
              e.currentTarget.style.boxShadow = '0 20px 60px rgba(0, 0, 0, 0.15)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 4px 16px rgba(0, 0, 0, 0.04)';
            }}
          >
            <div style={styles.statNumber}>10x</div>
            <div style={styles.statLabel}>Parallel Downloads</div>
          </div>
          <div 
            style={styles.statCard}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-10px)';
              e.currentTarget.style.boxShadow = '0 20px 60px rgba(0, 0, 0, 0.15)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 4px 16px rgba(0, 0, 0, 0.04)';
            }}
          >
            <div style={styles.statNumber}>100%</div>
            <div style={styles.statLabel}>SHA-512 Secure</div>
          </div>
          <div 
            style={styles.statCard}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-10px)';
              e.currentTarget.style.boxShadow = '0 20px 60px rgba(0, 0, 0, 0.15)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 4px 16px rgba(0, 0, 0, 0.04)';
            }}
          >
            <div style={styles.statNumber}>3-5s</div>
            <div style={styles.statLabel}>Cache Hit Time</div>
          </div>
        </div>
      </section>

      <section style={styles.section}>
        <div style={styles.container}>
          <h2 style={styles.sectionTitle}>Powerful Features</h2>
          <p style={styles.sectionSubtitle}>
            Everything you need for modern Node.js development, built with performance and reliability in mind.
          </p>
          <div style={styles.featuresGrid}>
            {features.map((feature, index) => {
              const Icon = feature.icon;
              const isActive = index === activeFeature;
              return (
                <div 
                  key={index} 
                  style={styles.featureCard(isActive)}
                  onMouseEnter={() => setActiveFeature(index)}
                >
                  <div style={styles.featureIcon(isActive)}>
                    <Icon size={36} />
                  </div>
                  <h3 style={styles.featureTitle(isActive)}>{feature.title}</h3>
                  <p style={styles.featureDescription(isActive)}>{feature.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section style={styles.sectionAlt}>
        <div style={styles.container}>
          <h2 style={styles.sectionTitle}>Performance Comparison</h2>
          <p style={styles.sectionSubtitle}>
            Real-world benchmarks showing how R_SNAPP outperforms traditional package managers.
          </p>
          <table style={styles.comparisonTable}>
            <thead>
              <tr>
                <th style={{...styles.tableHeader, borderTopLeftRadius: '20px'}}>Operation</th>
                <th style={styles.tableHeader}>npm v10</th>
                <th style={styles.tableHeader}>R_SNAPP</th>
                <th style={{...styles.tableHeader, borderTopRightRadius: '20px'}}>Improvement</th>
              </tr>
            </thead>
            <tbody>
              {[
                { op: 'First install (50 packages)', npm: '45s', snapp: '38s', imp: '15% faster' },
                { op: 'Reinstall (cached)', npm: '45s', snapp: '4s', imp: '90% faster' },
                { op: 'Parallel downloads', npm: '15', snapp: '10 (configurable)', imp: 'Optimized' },
                { op: 'Integrity verification', npm: 'SHA-1', snapp: 'SHA-512', imp: 'More secure' },
              ].map((row, i) => (
                <tr 
                  key={i} 
                  style={styles.tableRow}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = '#f8fafc';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = '#ffffff';
                  }}
                >
                  <td style={{...styles.tableCell, fontWeight: '700', color: '#334155'}}>{row.op}</td>
                  <td style={{...styles.tableCell, color: '#64748b'}}>{row.npm}</td>
                  <td style={{...styles.tableCell, color: '#6366f1', fontWeight: '700'}}>{row.snapp}</td>
                  <td style={{...styles.tableCell, color: '#10b981', fontWeight: '700'}}>{row.imp}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section style={{
        ...styles.section,
        background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 50%, #d946ef 100%)',
        textAlign: 'center',
        position: 'relative',
        overflow: 'hidden',
      }}>
        <div style={styles.heroPattern}></div>
        <div style={{ position: 'relative', zIndex: 1 }}>
          <h2 style={{ fontSize: '56px', fontWeight: '900', color: '#ffffff', marginBottom: '24px', letterSpacing: '-2px' }}>
            Ready to accelerate your workflow?
          </h2>
          <p style={{ fontSize: '24px', color: 'rgba(255, 255, 255, 0.95)', marginBottom: '48px' }}>
            Join developers who have already switched to faster, more reliable package management.
          </p>
          <button 
            style={{...styles.primaryButton, fontSize: '20px', padding: '24px 56px'}}
            onClick={() => setCurrentPage('docs')}
            onMouseEnter={(e) => {
              e.target.style.transform = 'translateY(-4px) scale(1.05)';
              e.target.style.boxShadow = '0 24px 60px rgba(0, 0, 0, 0.35)';
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = 'translateY(0) scale(1)';
              e.target.style.boxShadow = '0 12px 40px rgba(0, 0, 0, 0.25)';
            }}
          >
            <Download size={26} />
            Install R_SNAPP Now
          </button>
        </div>
      </section>
    </>
  );

  const renderDocs = () => (
    <div style={styles.docsContainer}>
      <aside style={styles.docsSidebar}>
        <div style={{ marginBottom: '32px' }}>
          <h3 style={{ fontSize: '15px', fontWeight: '800', color: '#1e293b', marginBottom: '16px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
            Documentation
          </h3>
          {docsSections.map(section => {
            const Icon = section.icon;
            return (
              <div
                key={section.id}
                style={styles.sidebarLink(activeDocSection === section.id)}
                onClick={() => {
                  setActiveDocSection(section.id);
                  document.getElementById(section.id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }}
                onMouseEnter={(e) => {
                  if (activeDocSection !== section.id) {
                    e.currentTarget.style.background = 'rgba(99, 102, 241, 0.05)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (activeDocSection !== section.id) {
                    e.currentTarget.style.background = 'transparent';
                  }
                }}
              >
                <Icon size={18} />
                {section.title}
              </div>
            );
          })}
        </div>
        <div style={{
          padding: '20px',
          background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
          borderRadius: '16px',
          color: '#ffffff',
        }}>
          <h4 style={{ fontSize: '16px', fontWeight: '800', marginBottom: '8px' }}>Need Help?</h4>
          <p style={{ fontSize: '14px', marginBottom: '16px', opacity: 0.9 }}>
            Join our community on GitHub for support.
          </p>
          <button
            onClick={() => window.open('https://github.com/jnt48/R-SNAPP/issues', '_blank')}
            style={{
              background: 'rgba(255, 255, 255, 0.2)',
              border: '1px solid rgba(255, 255, 255, 0.3)',
              color: '#ffffff',
              padding: '8px 16px',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '600',
              width: '100%',
            }}
          >
            Get Help
          </button>
        </div>
      </aside>

      <div style={styles.docsContent}>
        <div id="getting-started" style={styles.docSection}>
          <h1 style={styles.docTitle}>Getting Started with R_SNAPP</h1>
          <p style={styles.docText}>
            R_SNAPP is a lightning-fast, modern package manager for Node.js that provides intelligent caching, 
            parallel downloads, and cryptographic security. It's designed to be a drop-in replacement for npm 
            with significantly better performance.
          </p>
          <p style={styles.docText}>
            With 90% faster reinstalls, 10 parallel downloads, and SHA-512 integrity verification, R_SNAPP makes 
            package management faster and more reliable than ever before.
          </p>
        </div>

        <div id="installation" style={styles.docSection}>
          <h2 style={styles.docTitle}>Installation</h2>
          
          <h3 style={styles.docSubtitle}>Global Installation (Recommended)</h3>
          <p style={styles.docText}>
            Install R_SNAPP globally to use it across all your projects:
          </p>
          <div style={styles.commandBox}>
            <code>npm install -g r_snapp</code>
            <button
              style={styles.copyButton}
              onClick={() => copyToClipboard('npm install -g r_snapp', 'global')}
              onMouseEnter={(e) => e.target.style.background = '#4f46e5'}
              onMouseLeave={(e) => e.target.style.background = '#6366f1'}
            >
              {copiedCommand === 'global' ? <Check size={14} /> : <Copy size={14} />}
              {copiedCommand === 'global' ? 'Copied' : 'Copy'}
            </button>
          </div>

          <h3 style={styles.docSubtitle}>Verify Installation</h3>
          <p style={styles.docText}>
            After installation, verify that R_SNAPP is working correctly:
          </p>
          <div style={styles.codeSnippet}>
            <div>$ snapp --version</div>
            <div style={{ color: '#34d399' }}>snapp v1.0.1</div>
            <br />
            <div>$ snapp help</div>
            <div style={{ color: '#94a3b8' }}># Shows all available commands</div>
          </div>

          <h3 style={styles.docSubtitle}>System Requirements</h3>
          <p style={styles.docText}>
            <strong>Node.js:</strong> Version 14.0.0 or higher<br />
            <strong>npm:</strong> Version 6.0.0 or higher<br />
            <strong>OS:</strong> Windows, macOS, Linux (Ubuntu 24)
          </p>
        </div>

        <div id="commands" style={styles.docSection}>
          <h2 style={styles.docTitle}>Commands Reference</h2>
          
          <h3 style={styles.docSubtitle}>snapp init</h3>
          <p style={styles.docText}>
            Create a new package.json file in the current directory.
          </p>
          <div style={styles.codeSnippet}>
            $ snapp init
          </div>

          <h3 style={styles.docSubtitle}>snapp install</h3>
          <p style={styles.docText}>
            Install all dependencies listed in package.json.
          </p>
          <div style={styles.codeSnippet}>
            $ snapp install<br />
            $ snapp install --production  # Install only production dependencies<br />
            $ snapp install --force        # Clear cache and reinstall
          </div>

          <h3 style={styles.docSubtitle}>snapp add</h3>
          <p style={styles.docText}>
            Add a package to your project dependencies.
          </p>
          <div style={styles.codeSnippet}>
            $ snapp add express                    # Add to dependencies<br />
            $ snapp add jest --dev                 # Add to devDependencies<br />
            $ snapp add lodash@4.17.21             # Add specific version<br />
            $ snapp add axios chalk dotenv         # Add multiple packages
          </div>

          <h3 style={styles.docSubtitle}>snapp remove</h3>
          <p style={styles.docText}>
            Remove a package from your project.
          </p>
          <div style={styles.codeSnippet}>
            $ snapp remove lodash<br />
            $ snapp rm express         # Short alias
          </div>

          <h3 style={styles.docSubtitle}>snapp list</h3>
          <p style={styles.docText}>
            List all installed packages.
          </p>
          <div style={styles.codeSnippet}>
            $ snapp list<br />
            $ snapp ls             # Short alias
          </div>

          <h3 style={styles.docSubtitle}>snapp outdated</h3>
          <p style={styles.docText}>
            Check for outdated packages in your project.
          </p>
          <div style={styles.codeSnippet}>
            $ snapp outdated
          </div>

          <h3 style={styles.docSubtitle}>snapp cache</h3>
          <p style={styles.docText}>
            Manage the package cache.
          </p>
          <div style={styles.codeSnippet}>
            $ snapp cache              # Show cache info<br />
            $ snapp cache clean        # Clear the cache<br />
            $ snapp cache dir          # Show cache directory
          </div>
        </div>

        <div id="configuration" style={styles.docSection}>
          <h2 style={styles.docTitle}>Configuration</h2>
          
          <h3 style={styles.docSubtitle}>Environment Variables</h3>
          <p style={styles.docText}>
            R_SNAPP can be configured using environment variables:
          </p>

          <div style={{ marginBottom: '32px' }}>
            <h4 style={{ fontSize: '18px', fontWeight: '700', marginBottom: '12px', color: '#334155' }}>
              SNAPP_REGISTRY
            </h4>
            <p style={styles.docText}>
              Set a custom npm registry URL (default: https://registry.npmjs.org)
            </p>
            <div style={styles.codeSnippet}>
              export SNAPP_REGISTRY="https://registry.npmjs.org"<br />
              # Windows<br />
              set SNAPP_REGISTRY=https://registry.npmjs.org
            </div>
          </div>

          <div style={{ marginBottom: '32px' }}>
            <h4 style={{ fontSize: '18px', fontWeight: '700', marginBottom: '12px', color: '#334155' }}>
              SNAPP_PARALLEL
            </h4>
            <p style={styles.docText}>
              Maximum number of parallel downloads (default: 10)
            </p>
            <div style={styles.codeSnippet}>
              export SNAPP_PARALLEL=20<br />
              # Windows<br />
              set SNAPP_PARALLEL=20
            </div>
          </div>

          <div style={{ marginBottom: '32px' }}>
            <h4 style={{ fontSize: '18px', fontWeight: '700', marginBottom: '12px', color: '#334155' }}>
              SNAPP_DEBUG
            </h4>
            <p style={styles.docText}>
              Enable detailed debug logging (0 or 1)
            </p>
            <div style={styles.codeSnippet}>
              export SNAPP_DEBUG=1<br />
              # Windows<br />
              set SNAPP_DEBUG=1
            </div>
          </div>

          <h3 style={styles.docSubtitle}>Usage Examples</h3>
          <div style={styles.codeSnippet}>
            # Increase parallelism for fast networks<br />
            SNAPP_PARALLEL=20 snapp install<br />
            <br />
            # Debug mode for troubleshooting<br />
            SNAPP_DEBUG=1 snapp install<br />
            <br />
            # Use private registry<br />
            SNAPP_REGISTRY=https://npm.mycompany.com snapp install
          </div>
        </div>

        <div id="features" style={styles.docSection}>
          <h2 style={styles.docTitle}>Features</h2>
          
          <h3 style={styles.docSubtitle}>Parallel Downloads</h3>
          <p style={styles.docText}>
            R_SNAPP downloads up to 10 packages simultaneously by default, significantly reducing installation 
            time. You can configure this with the SNAPP_PARALLEL environment variable.
          </p>

          <h3 style={styles.docSubtitle}>Intelligent Caching</h3>
          <p style={styles.docText}>
            All downloaded packages are cached in ~/.snapp/cache. This provides:
          </p>
          <ul style={{ fontSize: '17px', lineHeight: '1.8', color: '#475569', marginBottom: '20px', paddingLeft: '24px' }}>
            <li>90% faster reinstalls (3-5 seconds vs 30-60 seconds)</li>
            <li>Offline installation for cached packages</li>
            <li>Reduced bandwidth usage</li>
            <li>Better reliability on unreliable networks</li>
          </ul>

          <h3 style={styles.docSubtitle}>SHA-512 Integrity Verification</h3>
          <p style={styles.docText}>
            Every package is verified using SHA-512 cryptographic hashing to ensure:
          </p>
          <ul style={{ fontSize: '17px', lineHeight: '1.8', color: '#475569', marginBottom: '20px', paddingLeft: '24px' }}>
            <li>Downloaded packages haven't been corrupted</li>
            <li>Packages haven't been tampered with</li>
            <li>Package authenticity</li>
            <li>Full npm registry compatibility</li>
          </ul>

          <h3 style={styles.docSubtitle}>Semantic Versioning Support</h3>
          <p style={styles.docText}>
            R_SNAPP supports all npm semver patterns:
          </p>
          <div style={styles.codeSnippet}>
            "express": "^4.17.0"       // Compatible: 4.x.x &gt;= 4.17.0<br />
            "lodash": "~4.17.21"       // Approximately: 4.17.x &gt;= 4.17.21<br />
            "react": "&gt;=16.8.0"        // Greater than or equal<br />
            "vue": "&gt;2.0.0"            // Greater than<br />
            "axios": "&lt;=1.6.0"         // Less than or equal<br />
            "chalk": "&lt;5.0.0"          // Less than<br />
            "dotenv": "16.0.3"         // Exact version<br />
            "yargs": "1.0.0 - 2.0.0"   // Version range<br />
            "commander": "*"           // Latest version
          </div>

          <h3 style={styles.docSubtitle}>Dependency Graph Analysis</h3>
          <p style={styles.docText}>
            R_SNAPP analyzes your entire dependency tree to:
          </p>
          <ul style={{ fontSize: '17px', lineHeight: '1.8', color: '#475569', marginBottom: '20px', paddingLeft: '24px' }}>
            <li>Detect circular dependencies</li>
            <li>Determine optimal installation order</li>
            <li>Resolve version conflicts</li>
            <li>Warn about potential issues</li>
          </ul>

          <h3 style={styles.docSubtitle}>Automatic Binary Linking</h3>
          <p style={styles.docText}>
            CLI tools are automatically linked in node_modules/.bin with proper permissions, 
            making them immediately available for use.
          </p>

          <h3 style={styles.docSubtitle}>Retry Logic</h3>
          <p style={styles.docText}>
            Network operations automatically retry up to 3 times with exponential backoff. 
            Timeout protection: 30s for metadata, 60s for downloads.
          </p>
        </div>

        <div id="troubleshooting" style={styles.docSection}>
          <h2 style={styles.docTitle}>Troubleshooting</h2>
          
          <h3 style={styles.docSubtitle}>Command Not Found</h3>
          <p style={styles.docText}>
            If you get "snapp: command not found", the package may not be in your PATH.
          </p>
          <div style={styles.codeSnippet}>
            # Check if installed<br />
            npm list -g r_snapp<br />
            <br />
            # Reinstall if needed<br />
            npm install -g r_snapp --force
          </div>

          <h3 style={styles.docSubtitle}>Network Errors</h3>
          <p style={styles.docText}>
            If you're getting "ENOTFOUND registry.npmjs.org" errors:
          </p>
          <div style={styles.codeSnippet}>
            # Check internet connection<br />
            ping registry.npmjs.org<br />
            <br />
            # Reset npm registry<br />
            npm config set registry https://registry.npmjs.org/<br />
            <br />
            # Clear DNS cache (Windows)<br />
            ipconfig /flushdns<br />
            <br />
            # Use debug mode<br />
            SNAPP_DEBUG=1 snapp install
          </div>

          <h3 style={styles.docSubtitle}>Slow Installation</h3>
          <p style={styles.docText}>
            First installs are slower as packages are downloaded and cached. Subsequent installs 
            should be 90% faster. If still slow:
          </p>
          <div style={styles.codeSnippet}>
            # Increase parallelism<br />
            SNAPP_PARALLEL=20 snapp install<br />
            <br />
            # Check cache<br />
            snapp cache
          </div>

          <h3 style={styles.docSubtitle}>Integrity Check Failed</h3>
          <p style={styles.docText}>
            If you get integrity check failures:
          </p>
          <div style={styles.codeSnippet}>
            # Clear cache and retry<br />
            snapp cache clean<br />
            snapp install --force
          </div>

          <h3 style={styles.docSubtitle}>Get Help</h3>
          <p style={styles.docText}>
            For more help, check our GitHub repository or open an issue:
          </p>
          <div style={{ display: 'flex', gap: '16px', marginTop: '24px' }}>
            <button
              onClick={() => window.open('https://github.com/jnt48/R-SNAPP', '_blank')}
              style={{
                background: '#6366f1',
                color: '#ffffff',
                border: 'none',
                padding: '12px 24px',
                borderRadius: '10px',
                cursor: 'pointer',
                fontSize: '15px',
                fontWeight: '600',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
              }}
            >
              <Github size={18} />
              GitHub Repository
            </button>
            <button
              onClick={() => window.open('https://www.npmjs.com/package/r_snapp', '_blank')}
              style={{
                background: '#10b981',
                color: '#ffffff',
                border: 'none',
                padding: '12px 24px',
                borderRadius: '10px',
                cursor: 'pointer',
                fontSize: '15px',
                fontWeight: '600',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
              }}
            >
              <Package size={18} />
              npm Package
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  return ( <>
     <Helmet>
        <title>R_SNAPP - Lightning-Fast Package Manager</title>
        <meta name="description" content="Install Node.js packages 90% faster with R_SNAPP" />
        <meta property="og:title" content="R_SNAPP - Lightning-Fast Package Manager" />
        <meta property="og:description" content="10 parallel downloads, 90% faster reinstalls, SHA-512 secure" />
      </Helmet>
    <div style={styles.container}>
      <header style={styles.header}>
        <nav style={styles.nav}>
          <div style={styles.logo} onClick={() => setCurrentPage('home')}>
            <Boxes size={32} />
            R_SNAPP
          </div>
          <div style={styles.navLinks}>
            <span 
              style={styles.navLink(currentPage === 'home')}
              onClick={() => setCurrentPage('home')}
              onMouseEnter={(e) => {
                if (currentPage !== 'home') e.target.style.color = '#6366f1';
              }}
              onMouseLeave={(e) => {
                if (currentPage !== 'home') e.target.style.color = '#64748b';
              }}
            >
              Home
            </span>
            <span 
              style={styles.navLink(currentPage === 'docs')}
              onClick={() => setCurrentPage('docs')}
              onMouseEnter={(e) => {
                if (currentPage !== 'docs') e.target.style.color = '#6366f1';
              }}
              onMouseLeave={(e) => {
                if (currentPage !== 'docs') e.target.style.color = '#64748b';
              }}
            >
              Documentation
            </span>
            <span 
              style={styles.navLink(false)}
              onClick={() => window.open('https://github.com/jnt48/R-SNAPP', '_blank')}
              onMouseEnter={(e) => e.target.style.color = '#6366f1'}
              onMouseLeave={(e) => e.target.style.color = '#64748b'}
            >
              <Github size={18} style={{ verticalAlign: 'middle', marginRight: '4px' }} />
              GitHub
            </span>
          </div>
        </nav>
      </header>

      {currentPage === 'home' ? renderHome() : renderDocs()}

      <footer style={styles.footer}>
        <div style={styles.footerContainer}>
          <div>
            <div style={styles.footerTitle}>R_SNAPP</div>
            <p style={{ color: '#64748b', fontSize: '15px', lineHeight: '1.7', marginBottom: '20px' }}>
              The modern, lightning-fast package manager for Node.js developers who value speed and reliability.
            </p>
            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                onClick={() => window.open('https://github.com/jnt48/R-SNAPP', '_blank')}
                style={{
                  background: 'rgba(255, 255, 255, 0.1)',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  color: '#ffffff',
                  padding: '10px',
                  borderRadius: '10px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <Github size={20} />
              </button>
              <button
                onClick={() => window.open('https://www.npmjs.com/package/r_snapp', '_blank')}
                style={{
                  background: 'rgba(255, 255, 255, 0.1)',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  color: '#ffffff',
                  padding: '10px',
                  borderRadius: '10px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <Package size={20} />
              </button>
            </div>
          </div>
          <div>
            <div style={styles.footerTitle}>Product</div>
            <span style={styles.footerLink} onClick={() => setCurrentPage('home')} onMouseEnter={(e) => e.target.style.color = '#ffffff'} onMouseLeave={(e) => e.target.style.color = '#94a3b8'}>
              <ChevronRight size={16} /> Features
            </span>
            <span style={styles.footerLink} onClick={() => setCurrentPage('docs')} onMouseEnter={(e) => e.target.style.color = '#ffffff'} onMouseLeave={(e) => e.target.style.color = '#94a3b8'}>
              <ChevronRight size={16} /> Documentation
            </span>
            <span style={styles.footerLink} onClick={() => window.open('https://github.com/jnt48/R-SNAPP/releases', '_blank')} onMouseEnter={(e) => e.target.style.color = '#ffffff'} onMouseLeave={(e) => e.target.style.color = '#94a3b8'}>
              <ChevronRight size={16} /> Changelog
            </span>
            <span style={styles.footerLink} onClick={() => window.open('https://www.npmjs.com/package/r_snapp', '_blank')} onMouseEnter={(e) => e.target.style.color = '#ffffff'} onMouseLeave={(e) => e.target.style.color = '#94a3b8'}>
              <ChevronRight size={16} /> npm Package
            </span>
          </div>
          <div>
            <div style={styles.footerTitle}>Resources</div>
            <span style={styles.footerLink} onClick={() => window.open('https://github.com/jnt48/R-SNAPP', '_blank')} onMouseEnter={(e) => e.target.style.color = '#ffffff'} onMouseLeave={(e) => e.target.style.color = '#94a3b8'}>
              <ChevronRight size={16} /> GitHub Repository
            </span>
            <span style={styles.footerLink} onClick={() => window.open('https://github.com/jnt48/R-SNAPP/issues', '_blank')} onMouseEnter={(e) => e.target.style.color = '#ffffff'} onMouseLeave={(e) => e.target.style.color = '#94a3b8'}>
              <ChevronRight size={16} /> Report Issues
            </span>
            <span style={styles.footerLink} onClick={() => setCurrentPage('docs')} onMouseEnter={(e) => e.target.style.color = '#ffffff'} onMouseLeave={(e) => e.target.style.color = '#94a3b8'}>
              <ChevronRight size={16} /> Get Started
            </span>
            <span style={styles.footerLink} onClick={() => window.open('https://github.com/jnt48', '_blank')} onMouseEnter={(e) => e.target.style.color = '#ffffff'} onMouseLeave={(e) => e.target.style.color = '#94a3b8'}>
              <ChevronRight size={16} /> Community
            </span>
          </div>
          <div>
            <div style={styles.footerTitle}>Legal</div>
            <span style={styles.footerLink} onClick={() => window.open('https://github.com/jnt48/R-SNAPP/blob/main/LICENSE', '_blank')} onMouseEnter={(e) => e.target.style.color = '#ffffff'} onMouseLeave={(e) => e.target.style.color = '#94a3b8'}>
              <ChevronRight size={16} /> License (MIT)
            </span>
            <span style={styles.footerLink} onMouseEnter={(e) => e.target.style.color = '#ffffff'} onMouseLeave={(e) => e.target.style.color = '#94a3b8'}>
              <ChevronRight size={16} /> Privacy Policy
            </span>
            <span style={styles.footerLink} onMouseEnter={(e) => e.target.style.color = '#ffffff'} onMouseLeave={(e) => e.target.style.color = '#94a3b8'}>
              <ChevronRight size={16} /> Terms of Service
            </span>
          </div>
        </div>
        <div style={styles.copyright}>
          © 2026 R_SNAPP. Created by <a href="https://github.com/jnt48" target="_blank" style={{ color: '#6366f1', textDecoration: 'none' }}>@jnt48</a>. 
          Lightning Fast • Reliable • Secure • Open Source
        </div>
      </footer>
    </div>
  </>);
}